import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { Dayjs } from 'dayjs';
import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers-pro';

import ConditionValueDatetimeField from '~/components/Common/Elements/ConditionValueDatetimeField';
import { hasSuggestedConditionValue, toggleSuggestedConditionValue } from '~/components/Common/conditionValueUtils';

type Props = GridRenderEditCellParams & {
  isDatetime: boolean;
  isDateOnly?: boolean;
  suggestedConditionValues?: Array<string>;
  error?: boolean;
  helperText?: React.ReactNode;
};

export default function VoucherConditionValueEditCell({
  id,
  field,
  value,
  isDatetime,
  isDateOnly = false,
  suggestedConditionValues = [],
  error = false,
  helperText,
}: Props) {
  const apiRef = useGridApiContext();
  const [conditionValue, setConditionValue] = useState(typeof value === 'string' ? value : String(value ?? ''));
  const visibleSuggestedValues = useMemo(
    () => [...new Set(suggestedConditionValues.map((suggestedValue) => suggestedValue.trim()).filter(Boolean))],
    [suggestedConditionValues],
  );

  useEffect(() => {
    setConditionValue(typeof value === 'string' ? value : String(value ?? ''));
  }, [value]);

  const commitConditionValue = useCallback(
    (nextValue: string) => {
      setConditionValue(nextValue);
      void apiRef.current.setEditCellValue({ id, field, value: nextValue });
    },
    [apiRef, field, id],
  );

  const handleToggleSuggestedValue = useCallback(
    (suggestedValue: string) => {
      commitConditionValue(toggleSuggestedConditionValue(conditionValue, suggestedValue));
    },
    [commitConditionValue, conditionValue],
  );

  const valueEditor = !isDatetime ? (
    <TextField
      size="small"
      value={conditionValue}
      onChange={(event) => commitConditionValue(event.target.value)}
      fullWidth
      variant="outlined"
      error={error}
      helperText={helperText}
    />
  ) : isDateOnly ? (
    <Box sx={{ px: 1, py: 0.5, width: '100%', minWidth: 260 }} onKeyDown={(e) => e.stopPropagation()}>
      <DatePicker
        value={conditionValue ? dayjs(conditionValue) : null}
        onChange={(newValue: Dayjs | null) => {
          commitConditionValue(newValue?.isValid() ? newValue.format('YYYY-MM-DD') : '');
        }}
        format="DD/MM/YYYY"
        slotProps={{
          textField: { size: 'small', fullWidth: true, required: true, error, helperText },
        }}
      />
    </Box>
  ) : (
    <ConditionValueDatetimeField
      value={conditionValue}
      onCommit={(iso) => {
        commitConditionValue(iso);
      }}
    />
  );

  if (visibleSuggestedValues.length === 0) {
    return valueEditor;
  }

  return (
    <Stack spacing={0.5} sx={{ width: '100%', py: 0.5 }}>
      {valueEditor}
      <Accordion disableGutters variant="outlined" sx={{ width: '100%', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 32, px: 1 }}>
          <Typography variant="caption">Suggested values</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
          <Stack spacing={0.5}>
            {visibleSuggestedValues.map((suggestedValue) => {
              const isSelected = hasSuggestedConditionValue(conditionValue, suggestedValue);
              return (
                <Stack
                  key={suggestedValue}
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere', minWidth: 0 }}>
                    {suggestedValue}
                  </Typography>
                  <Box>
                    <IconButton
                      size="small"
                      type="button"
                      onClick={() => handleToggleSuggestedValue(suggestedValue)}
                      aria-label={`${isSelected ? 'Remove' : 'Add'} ${suggestedValue}`}
                      title={`${isSelected ? 'Remove' : 'Add'} ${suggestedValue}`}
                    >
                      {isSelected ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
